import {
  GET_FLOWS_WITHOUT_PROJECTS_CLEANUP,
  GET_FLOWS_WITHOUT_PROJECTS_COUNT_FAILURE,
  GET_FLOWS_WITHOUT_PROJECTS_COUNT_REQUEST,
  GET_FLOWS_WITHOUT_PROJECTS_COUNT_SUCCESS,
  GET_FLOWS_WITHOUT_PROJECTS_FAILURE,
  GET_FLOWS_WITHOUT_PROJECTS_REQUEST,
  GET_FLOWS_WITHOUT_PROJECTS_SUCCESS,
  GET_FLOWS_SQL_AUTOMATIONS_FAILURE,
  GET_FLOWS_SQL_AUTOMATIONS_REQUEST,
  GET_FLOWS_SQL_AUTOMATIONS_SUCCESS,
  GET_MCP_FLOWS_FAILURE,
  GET_MCP_FLOWS_REQUEST,
  GET_MCP_FLOWS_SUCCESS,
} from 'js/constants/actionTypes';

type Action = {
  type: string;
  error?: string;
  result?: { flows: { id: string[] } | string[] };
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
};

type Meta = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
};

type WithoutProjects = {
  ids: string[];
  total: number;
  loading: boolean;
  error: string | null;
  meta: Meta;
};

type SqlAutomations = {
  ids: string[];
  byId: Record<string, true>;
  total: number;
  loading: boolean;
  error: string | null;
  meta: Meta;
};

type McpFlows = {
  ids: string[];
  byId: Record<string, true>;
  total: number;
  loading: boolean;
  error: string | null;
  meta: Meta;
};

export type FlowCategoriesReduxState = {
  withoutProjects: WithoutProjects;
  sqlAutomations: SqlAutomations;
  mcpFlows: McpFlows;
};

const initialSqlAutomations: SqlAutomations = {
  ids: [],
  byId: {},
  total: 0,
  loading: false,
  error: null,
  meta: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  },
};

const initialMcpFlows: McpFlows = {
  ids: [],
  byId: {},
  total: 0,
  loading: false,
  error: null,
  meta: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  },
};

const initialState: FlowCategoriesReduxState = {
  withoutProjects: {
    ids: [],
    total: 0,
    loading: false,
    error: null,
    meta: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
    },
  },
  sqlAutomations: initialSqlAutomations,
  mcpFlows: initialMcpFlows,
};

export const flowCategoriesReducer = (
  state: FlowCategoriesReduxState = initialState,
  action: Action,
) => {
  switch (action.type) {
    /**
     * Persist the Flows into ReduxStore
     */
    case GET_FLOWS_WITHOUT_PROJECTS_REQUEST: {
      return {
        ...state,
        withoutProjects: {
          ...state.withoutProjects,
          error: null,
          loading: true,
        },
      };
    }
    case GET_FLOWS_WITHOUT_PROJECTS_FAILURE: {
      return {
        ...state,
        withoutProjects: {
          ids: [],
          total: 0,
          loading: false,
          error: action?.error || 'An error occurred while fetching flows',
        },
      };
    }
    case GET_FLOWS_WITHOUT_PROJECTS_SUCCESS: {
      const flowsId = action.result?.flows?.id;

      return {
        ...state,
        withoutProjects: {
          ids: flowsId ?? [],
          total: action.totalItems ?? 0,
          loading: false,
          error: null,
          meta: {
            currentPage: action.currentPage ?? 1,
            totalPages: action.totalPages ?? 1,
            totalItems: action.totalItems ?? 0,
          },
        },
      };
    }
    case GET_FLOWS_WITHOUT_PROJECTS_CLEANUP: {
      return {
        ...state,
        withoutProjects: { ...initialState.withoutProjects },
      };
    }

    /**
     * Only persist the count of Flows
     */
    case GET_FLOWS_WITHOUT_PROJECTS_COUNT_REQUEST: {
      return {
        ...state,
        withoutProjects: {
          ...state.withoutProjects,
          total: 0,
          error: null,
          loading: true,
        },
      };
    }
    case GET_FLOWS_WITHOUT_PROJECTS_COUNT_FAILURE: {
      return {
        ...state,
        withoutProjects: {
          ...state.withoutProjects,
          total: 0,
          loading: false,
          error: action?.error || 'An error occurred while fetching flows',
        },
      };
    }
    case GET_FLOWS_WITHOUT_PROJECTS_COUNT_SUCCESS: {
      return {
        ...state,
        withoutProjects: {
          ...state.withoutProjects,
          error: null,
          loading: false,
          total: action?.totalItems ?? 0,
        },
      };
    }

    case GET_FLOWS_SQL_AUTOMATIONS_REQUEST: {
      return {
        ...state,
        sqlAutomations: {
          ...state.sqlAutomations,
          error: null,
          loading: true,
        },
      };
    }
    case GET_FLOWS_SQL_AUTOMATIONS_FAILURE: {
      return {
        ...state,
        sqlAutomations: {
          ...state.sqlAutomations,
          loading: false,
          error: action?.error || 'An error occurred while fetching SQL automations',
        },
      };
    }
    case GET_FLOWS_SQL_AUTOMATIONS_SUCCESS: {
      const rawFlows = action.result?.flows;
      const flowIds = Array.isArray(rawFlows)
        ? rawFlows
        : ((rawFlows as { id?: string[] })?.id ?? []);
      const ids = flowIds.map((id) => String(id));
      const byId = ids.reduce<Record<string, true>>((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});

      return {
        ...state,
        sqlAutomations: {
          ids,
          byId,
          total: action.totalItems ?? 0,
          loading: false,
          error: null,
          meta: {
            currentPage: action.currentPage ?? 1,
            totalPages: action.totalPages ?? 1,
            totalItems: action.totalItems ?? 0,
          },
        },
      };
    }

    case GET_MCP_FLOWS_REQUEST: {
      return {
        ...state,
        mcpFlows: {
          ...state.mcpFlows,
          error: null,
          loading: true,
        },
      };
    }
    case GET_MCP_FLOWS_FAILURE: {
      return {
        ...state,
        mcpFlows: {
          ...state.mcpFlows,
          loading: false,
          error: action?.error || 'An error occurred while fetching MCP flows',
        },
      };
    }
    case GET_MCP_FLOWS_SUCCESS: {
      const rawFlows = action.result?.flows;
      const flowIds = Array.isArray(rawFlows)
        ? rawFlows
        : ((rawFlows as { id?: string[] })?.id ?? []);
      const ids = flowIds.map((id) => String(id));
      const byId = ids.reduce<Record<string, true>>((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});

      return {
        ...state,
        mcpFlows: {
          ids,
          byId,
          total: action.totalItems ?? 0,
          loading: false,
          error: null,
          meta: {
            currentPage: action.currentPage ?? 1,
            totalPages: action.totalPages ?? 1,
            totalItems: action.totalItems ?? 0,
          },
        },
      };
    }

    default:
      return state;
  }
};
